/**
 * Web push on this device — the browser half of fieldiq-api 0524.
 *
 * ─── WHAT WORKS WHERE ───────────────────────────────────────────────────────
 *
 *   Android Chrome / desktop   push works in the browser tab.
 *   iPhone / iPad (iOS 16.4+)  push works ONLY in the app opened from the
 *                              Home Screen ("Add to Home Screen" in Safari).
 *                              A Safari tab has no PushManager at all.
 *   Capacitor native shell     not here — a native app registers through its
 *                              own plugin (none yet). Skipped.
 *
 * ─── THE LIFECYCLE ──────────────────────────────────────────────────────────
 *
 *   turnOnPush()      a TAP only (iOS refuses a permission prompt that is not
 *                     a direct user gesture): ask permission, subscribe with
 *                     the API's VAPID key, POST the subscription.
 *   resyncPush()      on every sign-in and app start, SILENTLY: only when
 *                     permission is already granted — re-subscribe and re-POST,
 *                     so a rotated key, a new user on the phone or a server
 *                     that disabled the device are all put right. Never prompts.
 *   forgetPushDevice() on sign-out, BEFORE the token goes: DELETE this device
 *                     on the API, then unsubscribe — so the next person to sign
 *                     in on this phone does not get the last person's alerts.
 *
 * `deviceId` is a random id kept in localStorage. If it is lost the browser
 * keeps the same subscription endpoint, and the API moves the row to the new
 * id rather than pushing the phone twice.
 */
import { api } from '../api/client';
import { unwrap } from './api-envelope';
import { isNative } from './platform';

const DEVICE_ID_KEY = 'fieldiq.pushDeviceId';

export type PushSupport =
  | 'supported'       // can subscribe here
  | 'ios_needs_home'  // iPhone/iPad in a browser tab: add to Home Screen first
  | 'unsupported'     // no push in this browser (or the native shell)
  ;

export function isIOS(): boolean {
  const ua = navigator.userAgent || '';
  // iPadOS reports itself as a Mac; touch points give it away.
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
}

export type DeviceKind = 'phone' | 'tablet' | 'computer';

/**
 * What to call this device in a sentence — "this phone", "this tablet",
 * "this computer". From the user agent, with iPadOS's desktop-Safari disguise
 * seen through by its touch points (isIOS above does the same).
 *   iPad, or an Android without "Mobile"      → tablet
 *   iPhone / iPod, Android Mobile, any "Mobi" → phone
 *   anything else                            → computer
 */
export function deviceKind(): DeviceKind {
  const ua = navigator.userAgent || '';
  const iPadOS = navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1;
  if (/iPad/.test(ua) || iPadOS) return 'tablet';
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? 'phone' : 'tablet';
  if (/iPhone|iPod|Mobi/.test(ua)) return 'phone';
  return 'computer';
}

/** "iPad" or "iPhone", for the Apple-only steps. */
export function appleDeviceName(): 'iPad' | 'iPhone' {
  return deviceKind() === 'tablet' ? 'iPad' : 'iPhone';
}

export function isStandalone(): boolean {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true;
  } catch {
    return false;
  }
}

export function pushSupport(): PushSupport {
  if (isNative) return 'unsupported';
  const capable = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (capable) return 'supported';
  if (isIOS() && !isStandalone()) return 'ios_needs_home';
  return 'unsupported';
}

/** 'default' | 'granted' | 'denied', or null where there is no Notification API. */
export function notificationPermission(): NotificationPermission | null {
  return 'Notification' in window ? Notification.permission : null;
}

let sessionDeviceId: string | null = null;

function newId(): string {
  const c: any = (globalThis as any).crypto;
  return typeof c?.randomUUID === 'function'
    ? c.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function pushDeviceId(): string {
  try {
    const have = localStorage.getItem(DEVICE_ID_KEY);
    if (have) return have;
    const id = newId();
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    // Storage refused (private mode): one id for this page's life; the API
    // moves the row by endpoint when a later page registers under another.
    sessionDeviceId ??= newId();
    return sessionDeviceId;
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function vapidKey(): Promise<string | null> {
  const res = await api.push.config();
  const cfg = unwrap<{ webPush: { enabled: boolean; publicKey: string | null } }>(res.data);
  return cfg?.webPush?.enabled && cfg.webPush.publicKey ? cfg.webPush.publicKey : null;
}

/** Subscribe (reusing a live subscription made with the same key) and register it. */
async function subscribeAndRegister(): Promise<void> {
  const key = await vapidKey();
  if (!key) throw new Error('Phone alerts are not switched on for FieldIQ yet.');
  const reg = await navigator.serviceWorker.ready;
  const appKey = urlBase64ToUint8Array(key);
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with a different key cannot be pushed with ours.
  const sameKey = (() => {
    const k = sub?.options?.applicationServerKey;
    if (!k) return false;
    const a = new Uint8Array(k as ArrayBuffer);
    return a.length === appKey.length && a.every((v, i) => v === appKey[i]);
  })();
  if (sub && !sameKey) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
  }
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) throw new Error('The browser returned an incomplete subscription.');
  await api.push.register({
    platform: 'web',
    deviceId: pushDeviceId(),
    subscription: { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } },
  });
}

/**
 * Turn alerts on for this device. Call ONLY from a tap.
 * Resolves to the permission outcome; throws with a readable message on failure.
 */
export async function turnOnPush(): Promise<'granted' | 'denied' | 'default'> {
  if (pushSupport() !== 'supported') throw new Error('This browser cannot receive alerts.');
  const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') return permission;
  await subscribeAndRegister();
  return 'granted';
}

/** On sign-in / app start: re-register silently if permission was already given. Never prompts, never throws. */
export async function resyncPush(): Promise<void> {
  try {
    if (pushSupport() !== 'supported' || Notification.permission !== 'granted') return;
    await subscribeAndRegister();
  } catch (e) {
    console.warn('[push] resync failed:', e);
  }
}

/** Turn alerts off for this device (or on sign-out). Never throws. */
export async function forgetPushDevice(): Promise<void> {
  try {
    if (pushSupport() !== 'supported') return;
    await api.push.unregister(pushDeviceId()).catch(() => {});
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    await sub?.unsubscribe().catch(() => {});
  } catch (e) {
    console.warn('[push] forget failed:', e);
  }
}

/**
 * The one-time "get alerts on this phone" prompt is shown once per person per
 * device. Dismissing it, or acting on it, records that here; Settings →
 * Notifications is always the way back.
 */
const PROMPT_KEY = (userId: string) => `fieldiq.pushPromptSeen.${userId}`;

export function pushPromptSeen(userId: string): boolean {
  try {
    return localStorage.getItem(PROMPT_KEY(userId)) !== null;
  } catch {
    return true; // storage refused: never nag
  }
}

export function markPushPromptSeen(userId: string): void {
  try {
    localStorage.setItem(PROMPT_KEY(userId), new Date().toISOString());
  } catch {
    /* storage refused */
  }
}
